import ContactProfileImage from '@app/components/ContactProfileImage'
import DeleteConfirmationModal from '@app/components/DeleteConfirmationModal'
import { NavTabItem, NavTabs } from '@app/components/NavTabs'
import MaintenancePage from '@app/routes/Maintenance/MaintenancePage'
import { ICurrentUserState } from '@app/store/modules/account'
import {
  IGlobalRolesState,
  IGlobalTeamsState,
} from '@app/store/modules/global'
import { bindFunctions } from '@app/utils/class'
import { formatPhoneNumber, humanize } from '@app/utils/formatting'
import { IconName, IconPrefix } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import * as React from 'react'
import DocumentTitle from 'react-document-title'
import { Link, Route, RouteComponentProps, Switch } from 'react-router-dom'
import {
  Card,
  CardBody,
  CardHeader,
  Container,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from 'reactstrap'
import { actionCreators, IUserViewState } from '../modules/userView'
import UserAccess from './UserAccess'
import UserPermissions from './UserPermissions'
import UserRoles from './UserRoles'
import UserTeams from './UserTeams'

interface IComponentRouterProps {
  id: string
}

type IComponentProps = IUserViewState &
  ICurrentUserState &
  IGlobalRolesState &
  IGlobalTeamsState &
  RouteComponentProps<IComponentRouterProps> &
  typeof actionCreators

export default class UserViewPage extends React.PureComponent<IComponentProps> {
  private userId: string

  constructor(props: IComponentProps) {
    super(props)

    bindFunctions(this)
  }

  public componentDidMount() {
    const { id } = this.props.match.params
    this.userId = id
    this.props.loadUser(id)
  }

  public componentDidUpdate(
    prevProps: IComponentProps,
    prevState: any,
    snapshot: any,
  ) {
    const { id } = this.props.match.params
    if (prevProps.item && prevProps.item.id !== id) {
      this.userId = id
      this.props.loadUser(id)
    }
  }

  public render() {
    if (!this.props.item || !this.props.user) {
      return <div />
    }

    return (
      <DocumentTitle title={this.props.item.fullName}>
        <Container className='mt-3'>
          <Card className='d-flex flex-column mt-2'>
            <CardHeader className='bg-secondary text-white'>
              {this._renderCardHeader()}
            </CardHeader>
            <CardBody>{this._renderCardBody()}</CardBody>
          </Card>
          {this._renderTabs()}
          <Switch>
            <Route
              exact
              path={this.props.match.path}
              render={() => <UserRoles {...this.props} />}
            />
            <Route
              exact
              path={`${this.props.match.path}/access`}
              render={() => <UserAccess {...this.props} />}
            />
            <Route
              exact
              path={`${this.props.match.path}/permissions`}
              render={() => <UserPermissions {...this.props} />}
            />
            <Route
              exact
              path={`${this.props.match.path}/teams`}
              render={() => <UserTeams {...this.props} />}
            />
            <Route component={MaintenancePage} />
          </Switch>
          <DeleteConfirmationModal
            isOpen={this.props.showConfirmDelete}
            itemName={this.props.item.fullName}
            onConfirm={() => this.props.deleteUser(this.props.item.id)}
            onToggleModal={() => this.props.toggleConfirmDelete(false)}
          />
        </Container>
      </DocumentTitle>
    )
  }

  private _renderTabs() {
    return (
      <div className='mb-3 mt-3'>
        <NavTabs>
          <NavTabItem
            exact
            iconName='object-group'
            label='Roles'
            to={this.props.match.url}
          />
          <NavTabItem
            iconName='users'
            label='Teams'
            to={`${this.props.match.url}/teams`}
          />
          <NavTabItem
            badgeCount={this.props.item.properties.size}
            iconName='user-lock'
            label='Permissions'
            to={`${this.props.match.url}/permissions`}
          />
          <NavTabItem
            iconName='lock'
            label='Access'
            to={`${this.props.match.url}/access`}
          />
        </NavTabs>
      </div>
    )
  }

  private _renderCardBody() {
    return (
      <div className='d-flex flex-row flex-wrap'>
        <ContactProfileImage contact={this.props.item.id} size={70} />

        <div className='d-flex flex-column ml-3 align-self-center'>
          <div style={{ fontWeight: 'bold' }}>{this.props.item.fullName}</div>
          <div>
            {this.props.item.company ? this.props.item.company.name : ''}
          </div>
          <div>{this.props.item.title}</div>
        </div>

        <div className='d-flex flex-row flex-wrap mt-2 mt-md-0 ml-md-auto'>
          {this._renderPhoneNumbers()}
          {this._renderEmailAddresses()}
        </div>
      </div>
    )
  }

  private _renderEmailAddresses() {
    return (
      <div>
        {this.props.item.emailAddresses.map((email: any, i) => {
          return (
            <div
              className='d-flex flex-row'
              style={{ fontSize: '0.875rem' }}
              key={`email-${i}`}
            >
              <div className='text-muted mr-2'>{humanize(email.type)}</div>
              <div className='ml-auto'>
                <a href={`mailto:${email.emailAddress}`}>
                  {email.emailAddress}
                </a>
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  private _renderPhoneNumbers() {
    return (
      <div className='mr-2'>
        {this.props.item.phoneNumbers.map((phone: any, i) => {
          return (
            <div
              className='d-flex flex-row'
              style={{ fontSize: '0.875rem' }}
              key={`phone-${i}`}
            >
              <div className='text-muted mr-2'>{humanize(phone.type)}</div>
              <div className='phone-number ml-auto'>
                {formatPhoneNumber(phone.phoneNumber)}
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  private _renderCardHeader() {
    if (!this.props.user) {
      return null
    }

    const { id } = this.props.user

    const connectionHeaderIcon: [IconPrefix, IconName] = ['fal', 'user']

    return (
      <div className='d-flex flex-row'>
        <span>
          <FontAwesomeIcon
            className='primary-color'
            icon={connectionHeaderIcon}
            fixedWidth
          />
          {this.props.item.user
            ? `User (${this.props.item.user.status})`
            : 'User'}
        </span>
        <div className='ml-auto'>
          <UncontrolledDropdown style={{ display: 'inline' }}>
            <DropdownToggle tag='a' className='px-1 text-center'>
              <FontAwesomeIcon icon={['fal', 'chevron-down']} fixedWidth />
            </DropdownToggle>
            {this._renderDropdown()}
          </UncontrolledDropdown>
        </div>
      </div>
    )
  }

  private _renderDropdown() {
    const menuItems = []

    if (this._canEdit()) {
      menuItems.push(
        <Link key='edit' to={`/admin/user/${this.userId}/edit`}>
          <DropdownItem>Edit</DropdownItem>
        </Link>,
      )
    }

    return <DropdownMenu>{menuItems}</DropdownMenu>
  }

  private _canEdit() {
    if (!this.props.user) {
      return false
    }

    if (
      this.props.item.user &&
      this.props.item.user.status === 'System' &&
      this.props.item.user.permissions.isAdmin === true
    ) {
      return false
    }

    return true
  }
}
